import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionLabel } from "@/components/site/bits";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check, X, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { scrollToSection } from "@/lib/scroll";

const QUESTIONS = [
  "Do you have a website?",
  "Do you use a CRM?",
  "Do you automate sales?",
  "Do you use AI customer support?",
];

export default function Quiz() {
  const [answers, setAnswers] = useState({});
  const [gate, setGate] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const answered = Object.keys(answers).length;
  const yes = Object.values(answers).filter(Boolean).length;
  const score = Math.round((yes / QUESTIONS.length) * 100);

  const setAns = (i, v) => setAnswers((a) => ({ ...a, [i]: v }));

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    try {
      await api.post("/leads", { ...form, source: "quiz", meta: { score, answers } });
      setDone(true);
      setGate(false);
      toast.success("Your AI Readiness report is ready!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const label = score >= 75 ? "AI Advanced" : score >= 50 ? "AI Ready" : score >= 25 ? "AI Emerging" : "AI Beginner";

  return (
    <section id="quiz" className="relative py-28 px-6" data-testid="quiz">
      <div className="mx-auto max-w-4xl">
        <Reveal>
          <SectionLabel>AI Readiness Quiz</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
            How <span className="text-gradient">AI-ready</span> is your business?
          </h2>
        </Reveal>

        <div className="mt-12 rounded-3xl glass p-8 md:p-12">
          {!done ? (
            <>
              <div className="space-y-4">
                {QUESTIONS.map((q, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <div className="flex items-center justify-between rounded-2xl bg-white/[0.03] border border-white/10 p-5" data-testid={`quiz-q-${i}`}>
                      <span className="text-white/85">{q}</span>
                      <div className="flex gap-2">
                        <button onClick={() => setAns(i, true)} data-testid={`quiz-q-${i}-yes`}
                          className={`grid h-9 w-9 place-items-center rounded-full transition-colors ${answers[i] === true ? "bg-[#00FFB2] text-[#0B1220]" : "bg-white/5 text-white/60"}`}>
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => setAns(i, false)} data-testid={`quiz-q-${i}-no`}
                          className={`grid h-9 w-9 place-items-center rounded-full transition-colors ${answers[i] === false ? "bg-red-500 text-white" : "bg-white/5 text-white/60"}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              <button
                disabled={answered < QUESTIONS.length}
                onClick={() => setGate(true)}
                data-testid="quiz-submit"
                className="mt-8 w-full rounded-full bg-gradient-to-r from-[#00E5FF] to-[#7B61FF] py-3.5 font-semibold text-[#0B1220] disabled:opacity-40 hover:scale-[1.01] transition-transform inline-flex items-center justify-center gap-2"
              >
                <Sparkles className="h-4 w-4" /> {answered < QUESTIONS.length ? `Answer all ${QUESTIONS.length} questions` : "Reveal My AI Readiness Score"}
              </button>
            </>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center" data-testid="quiz-result">
              <div className="mx-auto h-40 w-40 relative">
                <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                  <motion.circle cx="50" cy="50" r="42" fill="none" stroke="url(#g)" strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={264} initial={{ strokeDashoffset: 264 }} animate={{ strokeDashoffset: 264 - (264 * score) / 100 }} transition={{ duration: 1.4, ease: "easeOut" }} />
                  <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#00E5FF" /><stop offset="100%" stopColor="#00FFB2" /></linearGradient></defs>
                </svg>
                <div className="absolute inset-0 grid place-items-center font-display text-3xl font-bold">{score}%</div>
              </div>
              <div className="mt-5 font-display text-2xl font-bold text-gradient">{label}</div>
              <p className="mt-3 text-white/60 max-w-md mx-auto">Our team will send a detailed roadmap to accelerate your AI adoption. Check your inbox!</p>
              <button onClick={() => scrollToSection("contact")} className="mt-6 rounded-full glass px-6 py-3 text-sm hover:border-[#00E5FF]/50">Talk to an expert</button>
            </motion.div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {gate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="quiz-gate">
            <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, opacity: 0 }}
              className="w-full max-w-md rounded-3xl glass-strong p-8">
              <div className="flex items-center gap-2 text-[#00E5FF] text-sm mb-2"><Lock className="h-4 w-4" /> One step away</div>
              <h3 className="font-display text-2xl font-bold">Unlock your AI Readiness report</h3>
              <p className="text-sm text-white/60 mt-1">Enter your details to reveal your score & personalized roadmap.</p>
              <div className="mt-6 space-y-3">
                <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quiz-name" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" />
                <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quiz-email" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quiz-phone" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" />
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setGate(false)} className="flex-1 rounded-full glass py-3 text-sm">Cancel</button>
                <button onClick={submit} data-testid="quiz-gate-submit" className="flex-1 rounded-full bg-[#00E5FF] text-[#0B1220] py-3 font-semibold">See My Score</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
